import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import { RootState, useAppDispatch } from '../../../../support/store';
import { setSettings } from '../../../../support/slices/tileBuilderSlice';
import { initialSettings } from '../../../../types/tileBuilderTypes';


interface ColumnsModalProps {
  chartId: string;
  derivedFields: string[];
  open: boolean;
  onClose: () => void;
}

export default function TilePreviewColumnsModal({ chartId, derivedFields, open, onClose }: ColumnsModalProps) {
  const dispatch = useAppDispatch();
  const selectedColumns = useSelector(
    (state: RootState) => state.tileBuilder[chartId]?.tile?.settings?.selectedColumns || initialSettings.selectedColumns
  );

  const [localColumns, setLocalColumns] = useState<string[]>(selectedColumns || []);

  useEffect(() => {
    if (open) {
      setLocalColumns(
        (selectedColumns?.length ?? 0) === 0 ? derivedFields : [...(selectedColumns || [])]
      );
    }
  }, [open, selectedColumns, derivedFields]);

  const leftColumns = derivedFields.filter((f) => !localColumns.includes(f));

  const handleSaveColumns = () => {
    dispatch(setSettings({ chartId, settings: { selectedColumns: localColumns } }));
    onClose();
  };

  const handleToggleColumn = (column: string, isAdding: boolean) => {
    if (isAdding) setLocalColumns((prev) => [...prev, column]);
    else setLocalColumns((prev) => prev.filter((col) => col !== column));
  };

  const handleReorder = (index: number, direction: 'up' | 'down') => {
    const newColumns = [...localColumns];
    if (direction === 'up' && index > 0) {
      [newColumns[index - 1], newColumns[index]] = [newColumns[index], newColumns[index - 1]];
    } else if (direction === 'down' && index < newColumns.length - 1) {
      [newColumns[index], newColumns[index + 1]] = [newColumns[index + 1], newColumns[index]];
    }
    setLocalColumns(newColumns);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>Edit Columns</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={4}>
          <Box flex={1}>
            <Typography variant="h6">Available Columns</Typography>
            <List>
              {leftColumns.map((column) => (
                <ListItemButton key={column} onClick={() => handleToggleColumn(column, true)}>
                  <ListItemIcon>
                    <Checkbox checked={false} />
                  </ListItemIcon>
                  <ListItemText primary={column} />
                </ListItemButton>
              ))}
            </List>
          </Box>

          <Box flex={1}>
            <Typography variant="h6">Selected Columns</Typography>
            <List>
              {localColumns.map((column, index) => (
                <ListItem key={column}>
                  <ListItemIcon>
                    <Checkbox checked={true} onChange={() => handleToggleColumn(column, false)} />
                  </ListItemIcon>
                  <ListItemText primary={column} />
                  <Box>
                    <Button onClick={() => handleReorder(index, 'up')} disabled={index === 0} size="small">
                      ↑
                    </Button>
                    <Button onClick={() => handleReorder(index, 'down')} disabled={index === localColumns.length - 1} size="small">
                      ↓
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveColumns}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}